import {
  Card,
  EmptyState,
  FormField,
  Input,
  ListSkeleton,
  MenuList,
  MenuListItem,
  Page,
  PageHeader,
  Select,
} from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounced } from '../lib/use-debounced';
import { useSession } from '../session/session';
import { MIN_SEARCH_LENGTH, searchQuery, useHitOrigin } from './api';
import { rememberHit } from './recent';

export interface SearchParams {
  q?: string;
  owner?: string;
}

/** Reads the search params of `/search`; anything unexpected is dropped. */
export function validateSearchParams(input: Record<string, unknown>): SearchParams {
  const text = (value: unknown) =>
    typeof value === 'string' && value.length <= 200 ? value : undefined;
  return { q: text(input.q), owner: text(input.owner) };
}

/** All hits of a term, narrowed to one module or the kernel (docs/architecture/search.md). */
export function SearchPage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const params: SearchParams = useSearch({ strict: false });
  const [text, setText] = useState(params.q ?? '');
  const term = useDebounced(text.trim(), 300);
  const owner = params.owner;
  const origin = useHitOrigin();
  // The unfiltered answer names the origins to narrow down to.
  const all = useQuery(searchQuery(term));
  const filtered = useQuery({ ...searchQuery(term, owner), enabled: !!owner && term.length >= 2 });
  const results = owner ? filtered : all;

  useEffect(() => {
    if (term === (params.q ?? '')) return;
    void navigate({ to: '/search', search: { q: term || undefined, owner }, replace: true });
  }, [term, owner, params.q, navigate]);

  const origins = new Map<string, string>();
  for (const hit of all.data?.items ?? []) {
    const { group, label } = origin(hit);
    origins.set(
      hit.kind === 'record' ? hit.owner : 'core',
      group.startsWith('core.') ? t('shell.search.kinds.core') : label,
    );
  }
  if (owner && !origins.has(owner))
    origins.set(owner, owner === 'core' ? t('shell.search.kinds.core') : owner);
  const hits = term.length >= MIN_SEARCH_LENGTH ? (results.data?.items ?? []) : [];

  return (
    <Page>
      <PageHeader title={t('search.title')} description={t('search.description')} />
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 md:grid-cols-[1fr_16rem]">
          <FormField label={t('search.term')}>
            {(field) => (
              <Input
                {...field}
                type="search"
                autoComplete="off"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('search.origin')}>
            {(field) => (
              <Select
                id={field.id}
                value={owner ?? 'all'}
                onValueChange={(value) =>
                  void navigate({
                    to: '/search',
                    search: { q: term || undefined, owner: value === 'all' ? undefined : value },
                    replace: true,
                  })
                }
                options={[
                  { value: 'all', label: t('search.allOrigins') },
                  ...[...origins].map(([value, label]) => ({ value, label })),
                ]}
              />
            )}
          </FormField>
        </div>

        {term.length < MIN_SEARCH_LENGTH ? (
          <EmptyState
            icon="search"
            title={t('search.startTitle')}
            description={t('search.startDescription')}
          />
        ) : results.isPending ? (
          <ListSkeleton rows={5} icon title={false} />
        ) : hits.length === 0 ? (
          <EmptyState
            icon="search"
            title={t('search.emptyTitle')}
            description={t('search.emptyDescription')}
          />
        ) : (
          <Card>
            <MenuList aria-label={t('search.results', { count: hits.length })}>
              {hits.map((hit) => {
                const from = origin(hit);
                return (
                  <MenuListItem
                    key={`${hit.entity}:${hit.id}`}
                    icon={from.icon}
                    href={hit.path}
                    description={hit.subtitle ?? undefined}
                    trailing={<span className="text-xs text-fg-subtle">{from.label}</span>}
                    onClick={() =>
                      rememberHit(session.account.id, {
                        title: hit.title,
                        path: hit.path,
                        icon: from.icon,
                      })
                    }
                  >
                    {hit.title}
                  </MenuListItem>
                );
              })}
            </MenuList>
          </Card>
        )}
      </div>
    </Page>
  );
}
